<script setup lang="ts">
import {
    computed, defineAsyncComponent, onMounted, ref, useAttrs,
} from 'vue';

// Imported from the module rather than the composables barrel: that barrel imports back
// into the design system, and the cycle leaves the re-exports uninitialised.
import { useScannerInput, SCANNER_INPUT_ATTRIBUTE } from '@/composables/useScannerInput';
import { trimScan } from '@/utils/scannerKeyboardLayout';
import type { ScannerScanType } from '@/utils/scannerKeyboardLayout';
import BTextField from './BTextField.vue';
import BIcon from './BIcon.vue';
import BBtnIcon from './BBtnIcon.vue';
import { useTranslation } from '@/composables/useTranslation';
import { unlockBeep } from '@/utils/beep';

type PropsType = {
    modelValue?: string;
    label?: string;
    icon?: string;

    /** Offers reading QR codes and barcodes with the device camera, e.g. on a phone. */
    camera?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    modelValue: '',
    icon: 'mdi-qrcode-scan',
    camera: true,
});

const emit = defineEmits<{
    'update:modelValue': [value: string];

    /**
     * The scanned burst. `keys` carries the physical key presses behind it so the consumer can
     * re-read a mangled code; it is empty for a value that was typed or pasted.
     */
    scan: [scan: ScannerScanType];
}>();

defineOptions({ inheritAttrs: false });

// Loaded on demand: it pulls in the camera library and its WebAssembly decoder.
const BCameraScanDialog = defineAsyncComponent(() => import('../composite/BCameraScanDialog.vue'));

const attrs = useAttrs();
const { t } = useTranslation();

const isDisabled = computed(() => attrs.disabled !== undefined && attrs.disabled !== false);

/**
 * On a phone or tablet focusing the field opens the on-screen keyboard over the page, so the
 * field neither autofocuses nor takes the focus back after a scan there; a tap still focuses it.
 */
const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

const textFieldAttrs = computed(() => {
    if (!isTouchDevice) {
        return attrs;
    }

    const { autofocus: _autofocus, ...rest } = attrs;

    return rest;
});

const isCameraOpen = ref(false);

const openCamera = (): void => {
    // The click is the user gesture that lets the beep play later (iOS Safari).
    unlockBeep();
    isCameraOpen.value = true;
};

/**
 * A camera read carries no key presses, the same as a typed or pasted value.
 */
const emitCameraScan = (value: string): void => {
    emit('scan', { value: value.trim(), keys: [] });
};

/**
 * A burst coming from a barcode scanner is rebuilt from the physical keys, so the value is
 * correct even when the operating system runs a non-US keyboard layout; anything typed or
 * pasted is emitted as entered.
 */
const { handleKeydown, takeScan, takeBurst } = useScannerInput({
    // Plenty of scanners send no Enter suffix, so the burst also closes on its own.
    onBurstEnd: burst => {
        if (burst.isLongEnough && burst.isFast) {
            emit('scan', trimScan(burst));
        }
    },
});

const textFieldRef = ref<InstanceType<typeof BTextField> | null>(null);

const emitScan = (): void => {
    emit('scan', trimScan(takeScan(props.modelValue)));
};

/**
 * Some scanners suffix a code with Tab rather than Enter. Its default action moves the focus
 * off the field, so the burst has to be closed on keydown, before that happens, and the key
 * swallowed.
 *
 * The burst is read first and the key is only swallowed once it turns out to be machine
 * input: the shadow buffer fills up on anything typed too, so deciding on "a buffer exists"
 * would trap the focus of anyone who types a code by hand and tabs on.
 */
const handleScannerKeydown = (event: KeyboardEvent): void => {
    handleKeydown(event);

    if (event.key !== 'Tab') {
        return;
    }

    const burst = takeBurst();

    if (!burst.isLongEnough || !burst.isFast) {
        return;
    }

    event.preventDefault();
    emit('scan', trimScan(burst));
};

const focus = (): void => {
    // Consumers refocus the field after every scan; behind the camera or on a touch device that
    // would only pop up the on-screen keyboard.
    if (isCameraOpen.value || isTouchDevice) {
        return;
    }

    const input = textFieldRef.value?.$el?.querySelector('input');

    input?.focus();
};

onMounted(() => {
    // Marks the field as self-handling for useGlobalScannerInput.
    textFieldRef.value?.$el?.querySelector('input')?.setAttribute(SCANNER_INPUT_ATTRIBUTE, '');
});

defineExpose({ focus });
</script>

<template>
    <BTextField
        ref="textFieldRef"
        :model-value="modelValue"
        :label="label"
        v-bind="textFieldAttrs"
        @update:model-value="emit('update:modelValue', String($event))"
        @keydown="handleScannerKeydown"
        @keyup.enter="emitScan"
    >
        <template #prepend-inner>
            <BIcon
                :icon="icon"
                size="small"
                class="opacity-50"
            />
        </template>
        <template
            v-if="camera && !isDisabled"
            #append-inner
        >
            <BBtnIcon
                icon="mdi-camera"
                :tooltip="t('scanner.openCamera')"
                density="compact"
                @click.stop="openCamera"
            />
        </template>
    </BTextField>

    <BCameraScanDialog
        v-if="camera && isCameraOpen"
        v-model="isCameraOpen"
        :title="label"
        @scan="emitCameraScan"
    />
</template>
